import { createContext, useContext, useLayoutEffect, useState } from "react"
import type { ReactNode } from "react"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

const createUploadStore = () =>
  createStore<{
    pending: boolean
    sources: ReadonlySet<symbol>
    setPending: (id: symbol, pending: boolean) => void
  }>((set) => ({
    pending: false,
    sources: new Set(),
    setPending: (id, pending) =>
      set((state) => {
        if (state.sources.has(id) === pending) return state
        const sources = new Set(state.sources)
        if (pending) sources.add(id)
        else sources.delete(id)
        return { sources, pending: sources.size > 0 }
      }),
  }))

const UploadContext = createContext<ReturnType<
  typeof createUploadStore
> | null>(null)
const EMPTY_UPLOAD_STORE = createUploadStore()

export const ResourceUploadScope = ({
  children,
  onPendingChange,
}: {
  children: ReactNode
  onPendingChange?: (pending: boolean) => void
}) => {
  const [store] = useState(createUploadStore)
  useLayoutEffect(() => {
    onPendingChange?.(store.getState().pending)
    const unsubscribe = store.subscribe((state, previous) => {
      if (state.pending !== previous.pending) onPendingChange?.(state.pending)
    })
    return () => {
      unsubscribe()
      onPendingChange?.(false)
    }
  }, [store, onPendingChange])
  return <UploadContext value={store}>{children}</UploadContext>
}

export const useResourceUploadStatus = () => {
  const store = useContext(UploadContext) || EMPTY_UPLOAD_STORE
  const pending = useStore(store, (state) => state.pending)
  return { pending, isPending: () => store.getState().pending }
}

export const useResourceUploadActivity = (pending: boolean) => {
  const store = useContext(UploadContext)
  const [id] = useState(() => Symbol("resource-upload"))
  useLayoutEffect(() => {
    store?.getState().setPending(id, pending)
  }, [store, id, pending])
  useLayoutEffect(
    () => () => {
      store?.getState().setPending(id, false)
    },
    [store, id],
  )
}
